import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Mail, Send, Loader2, ShieldCheck, AlertTriangle, CheckCircle2, Info, Inbox,
} from "lucide-react";

/* ------------------------------------------------------------------------- */
/* Status pill helpers — never reveal SMTP secrets in the UI                  */
/* ------------------------------------------------------------------------- */
const MODE_STYLES = {
  live: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Live SMTP" },
  outbox_only: { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Outbox-only" },
  config_missing: { cls: "bg-rose-50 text-rose-800 border-rose-200", label: "Config missing" },
};

const TEST_STATUS_STYLES = {
  sent: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "✅ Sent" },
  failed: { cls: "bg-rose-50 text-rose-800 border-rose-200", label: "❌ Failed" },
  outbox_only: { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "📥 Outbox-only" },
  config_missing: { cls: "bg-rose-50 text-rose-800 border-rose-200", label: "❌ Config missing" },
  queued: { cls: "bg-slate-50 text-slate-700 border-slate-200", label: "⏳ Queued" },
};

function Pill({ status, dict, testid }) {
  const s = dict[status] || { cls: "bg-slate-50 text-slate-600 border-slate-200", label: status };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

/* ------------------------------------------------------------------------- */
/* Platform → Settings → SMTP                                                  */
/* ------------------------------------------------------------------------- */
export default function PlatformSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.get("/platform/smtp/config");
      setConfig(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load SMTP config");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const runTest = async () => {
    if (!testTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testTo)) {
      toast.error("Enter a valid recipient email address");
      return;
    }
    setTesting(true);
    setLastResult(null);
    try {
      const r = await client.post("/platform/smtp/test", { to_email: testTo });
      setLastResult(r.data);
      if (r.data.status === "sent") toast.success("Test email sent — check the recipient inbox.");
      else if (r.data.status === "outbox_only") toast.message("Outbox-only: SMTP_ENABLED is false. Email saved to Outbox.");
      else if (r.data.status === "config_missing") toast.error("SMTP enabled but config is incomplete. See result panel.");
      else if (r.data.status === "failed") toast.error("SMTP send failed. See result panel.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test request failed");
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-6 p-4 md:p-8" data-testid="platform-settings-page">
      <div>
        <div className="overline text-teal-700">FlowQA Owner</div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">Settings</h1>
        <p className="mt-1 text-sm text-slate-600">Platform-level configuration. Visible only to FlowQA Owners.</p>
      </div>

      {/* SMTP card */}
      <Card className="border-slate-200 p-6" data-testid="smtp-card">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700"><Mail className="size-5" /></div>
            <div>
              <div className="font-heading text-lg font-bold text-slate-900">SMTP / Email Delivery</div>
              <p className="text-sm text-slate-600">All FlowQA emails (onboarding, hold-point reviews, survey requests, more-info, etc.) flow through the same SMTP path. Email Outbox is always written as a fallback and audit log.</p>
            </div>
          </div>
          {config && (
            <div className="flex flex-col items-end gap-2">
              <Pill status={config.mode} dict={MODE_STYLES} testid={`smtp-mode-${config.mode}`} />
              <button
                type="button"
                onClick={loadConfig}
                disabled={loading}
                className="text-xs text-teal-700 underline-offset-2 hover:underline"
                data-testid="smtp-reload-btn"
              >
                {loading ? "Reloading…" : "Reload"}
              </button>
            </div>
          )}
        </div>

        {/* Config grid */}
        {loading && !config && (
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="size-4 animate-spin" /> Loading…</div>
        )}
        {config && (
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="smtp-config-grid">
            {[
              ["Enabled", String(config.enabled), config.enabled ? "text-emerald-700" : "text-amber-700"],
              ["Host", config.host || "—", "text-slate-900"],
              ["Port", String(config.port), "text-slate-900"],
              ["Username", config.username || "—", "text-slate-900"],
              ["Password", config.password_set ? "•••••• (set)" : "— (not set)", config.password_set ? "text-emerald-700" : "text-amber-700"],
              ["From", `${config.from_name} <${config.from_email}>`, "text-slate-900"],
              ["TLS Mode", config.port === 465 ? "Implicit (SMTP_SSL)" : (config.use_tls ? "STARTTLS" : "Plain"), "text-slate-900"],
              ["Outbox Fallback", "Always on", "text-emerald-700"],
            ].map(([l, v, cls]) => (
              <div key={l} className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={`smtp-cfg-${l.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
                <div className={`mt-0.5 truncate text-sm font-semibold ${cls}`}>{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Mode hint */}
        {config?.mode === "outbox_only" && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="smtp-mode-hint">
            <Info className="mt-0.5 size-4 shrink-0" />
            <div>
              <b>Outbox-only mode is active.</b> No real emails are being sent. To enable delivery, set <code className="rounded bg-white px-1">SMTP_ENABLED=true</code> and the SMTP credentials in <code className="rounded bg-white px-1">/app/backend/.env</code>, then restart the backend.
            </div>
          </div>
        )}
        {config?.mode === "config_missing" && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900" data-testid="smtp-mode-hint">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div><b>SMTP_ENABLED=true</b> but one or more credentials (host/username/password) are missing. Emails will be saved to Outbox with status <code>config_missing</code> until you complete the configuration.</div>
          </div>
        )}
        {config?.mode === "live" && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="smtp-mode-hint">
            <ShieldCheck className="mt-0.5 size-4 shrink-0" />
            <div><b>Live SMTP is configured.</b> Real emails are sent via {config.host}:{config.port}. Each send is also recorded in the Email Outbox.</div>
          </div>
        )}

        {/* Test SMTP */}
        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4" data-testid="smtp-test-card">
          <div className="font-heading text-base font-bold text-slate-900">Send Test Email</div>
          <p className="text-xs text-slate-600">Uses the same delivery path as production emails. Writes a record to the Email Outbox tagged <code>smtp_test</code> and adds an entry to the audit trail.</p>
          <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-end">
            <div className="flex-1">
              <Label htmlFor="smtp-test-to">Recipient email</Label>
              <Input
                id="smtp-test-to"
                type="email"
                placeholder="you@your-domain.com"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                data-testid="smtp-test-recipient-input"
                className="h-10"
              />
            </div>
            <Button
              onClick={runTest}
              disabled={testing || !testTo}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="smtp-test-send-btn"
            >
              {testing ? (
                <><Loader2 className="mr-2 size-4 animate-spin" /> Sending…</>
              ) : (
                <><Send className="mr-2 size-4" /> Send Test Email</>
              )}
            </Button>
          </div>

          {/* Last result */}
          {lastResult && (
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="smtp-test-result">
              <div className="flex flex-wrap items-center gap-2">
                <Pill status={lastResult.status} dict={TEST_STATUS_STYLES} testid={`smtp-test-status-${lastResult.status}`} />
                <span className="text-xs text-slate-600">to <b className="text-slate-900">{lastResult.to}</b></span>
                {lastResult.smtp_sent_at && (
                  <span className="text-xs text-slate-500">· delivered at {String(lastResult.smtp_sent_at).replace("T", " ").split(".")[0]}</span>
                )}
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                <div className="rounded border border-slate-200 bg-white p-2 text-xs">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Provider</div>
                  <div className="font-semibold text-slate-900">{lastResult.provider}</div>
                </div>
                <div className="rounded border border-slate-200 bg-white p-2 text-xs">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Outbox record id</div>
                  <div className="truncate font-mono text-[11px] text-slate-700">{lastResult.outbox_id}</div>
                </div>
                {lastResult.error_message && (
                  <div className="md:col-span-2 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-900" data-testid="smtp-test-error-message">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">Error</div>
                    <div className="mt-0.5 font-mono">{lastResult.error_message}</div>
                  </div>
                )}
              </div>
              {lastResult.status === "sent" && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                  <CheckCircle2 className="mt-0.5 size-3.5 shrink-0" />
                  <div>Real email was delivered. Check the recipient&apos;s inbox (and Spam/Junk) and confirm. Email Outbox also recorded this send.</div>
                </div>
              )}
              {lastResult.status === "outbox_only" && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  <Inbox className="mt-0.5 size-3.5 shrink-0" />
                  <div>SMTP is disabled. No real email was sent. The record is in the Email Outbox only.</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick-link to Outbox would normally go here but Owner Console doesn't carry the Outbox page; show note */}
        <div className="mt-4 text-xs text-slate-500">
          Every send (real or fallback) lives in the <b>emails</b> collection with the recipient, subject, status and timestamp. Logs never contain the SMTP password.
        </div>
      </Card>
    </div>
  );
}
